export type StatVariant = "neutral" | "positive" | "streak";

export type DashboardStat = {
  label: string;
  value: string;
  subtitle: string;
  variant: StatVariant;
};

export const getTeacherDashboardStats = (classCount: number): DashboardStat[] => [
  {
    label: "Active classes",
    value: `${classCount}`,
    subtitle: "on your account",
    variant: "neutral",
  },
  { label: "Submissions", value: "86", subtitle: "this term", variant: "neutral" },
  {
    label: "Avg class score",
    value: "74%",
    subtitle: "across all classes",
    variant: "positive",
  },
];

export type DailyReading = {
  date: string;
  label: string;
  questions: number;
  correct: number;
  incorrect: number;
  accuracy: number;
};

export type ActivityHeatmapDay = {
  date: string;
  fullDateLabel: string;
  questions: number;
};

const getMondayBasedDayIndex = (date: Date): number => {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
};

export const getPeakActivityDay = (days: ActivityHeatmapDay[]): ActivityHeatmapDay | null => {
  let peak: ActivityHeatmapDay | null = null;

  for (const day of days) {
    if (!peak || day.questions > peak.questions) {
      peak = day;
    } else if (day.questions === peak.questions && day.date > peak.date) {
      peak = day;
    }
  }

  return peak && peak.questions > 0 ? peak : null;
};

export const getHeatmapCellColor = (questions: number, maxQuestions: number): string => {
  if (questions === 0) return "#eef2f0";

  const intensity = questions / maxQuestions;
  if (intensity <= 0.25) return "#c6ebd9";
  if (intensity <= 0.5) return "#8fd9b8";
  if (intensity <= 0.75) return "#4fbf8a";
  return "#1d9e75";
};

export const buildActivityHeatmapWeeks = (
  days: ActivityHeatmapDay[]
): (ActivityHeatmapDay | null)[][] => {
  const firstDate = new Date(`${days[0].date}T12:00:00`);
  const startPadding = getMondayBasedDayIndex(firstDate);

  const cells: (ActivityHeatmapDay | null)[] = [
    ...Array.from({ length: startPadding }, () => null),
    ...days,
  ];

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const weeks: (ActivityHeatmapDay | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }

  return weeks;
};

// Each week column is 16px wide with a 5px gap (must match ActivityHeatmap.scss).
const HEATMAP_WEEK_SLOT_WIDTH_PX = 21;
const HEATMAP_AXIS_LABEL_CHAR_WIDTH_PX = 8;

const formatHeatmapAxisLabel = (date: Date): string => {
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${month} '${year}`;
};

// Labels are wider than a single week column, so estimate how many columns each spans.
const getHeatmapAxisLabelWeekSpan = (label: string): number =>
  Math.ceil((label.length * HEATMAP_AXIS_LABEL_CHAR_WIDTH_PX) / HEATMAP_WEEK_SLOT_WIDTH_PX);

export const buildActivityHeatmapAxisLabels = (
  weeks: (ActivityHeatmapDay | null)[][]
): Map<number, string> => {
  const candidates: { weekIndex: number; label: string }[] = [];
  let lastMonth = -1;
  let lastYear = -1;

  weeks.forEach((week, weekIndex) => {
    const firstDay = week.find((day) => day !== null);
    if (!firstDay) return;

    const date = new Date(`${firstDay.date}T12:00:00`);
    const month = date.getMonth();
    const year = date.getFullYear();

    if (month !== lastMonth || year !== lastYear) {
      candidates.push({ weekIndex, label: formatHeatmapAxisLabel(date) });
      lastMonth = month;
      lastYear = year;
    }
  });

  const labels = new Map<number, string>();
  let minLaterWeekIndex = Infinity;

  // Walk newest → oldest so overlapping labels drop the earlier month, not the later one.
  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    const { weekIndex, label } = candidates[index];
    const weekSpan = getHeatmapAxisLabelWeekSpan(label);

    if (weekIndex + weekSpan <= minLaterWeekIndex) {
      labels.set(weekIndex, label);
      minLaterWeekIndex = weekIndex;
    }
  }

  return labels;
};

export type StudyHabitsPeriod = {
  questionsDone: string;
  questionsDetail: string;
  correct: number;
  incorrect: number;
  avgScore: number;
  peerAvg: number;
  avgScoreDetail: string;
};

export type TopicStat = {
  moduleId?: number;
  name: string;
  score: number;
  questionsDone: number;
};

export const PLACEHOLDER_TOPIC_STATS: TopicStat[] = [
  { name: "Retina", score: 82, questionsDone: 214 },
  { name: "Cornea & external disease", score: 78, questionsDone: 187 },
  { name: "Oculoplastics", score: 74, questionsDone: 98 },
  { name: "Cataract & lens", score: 71, questionsDone: 163 },
  { name: "Neuro-ophthalmology", score: 67, questionsDone: 142 },
  { name: "Strabismus & paediatrics", score: 63, questionsDone: 119 },
  { name: "Uveitis & ocular inflammation", score: 59, questionsDone: 88 },
  { name: "Glaucoma", score: 54, questionsDone: 273 },
];

export type AllTimeStudentStats = {
  totalDone: number;
  correctAnswers: number;
  incorrectAnswers: number;
  allTimeAvgScore: number;
  starredQuestions: number;
  peerAvg: number;
  currentStreak: number;
  bestStreak: number;
};

export type PlatformStreakStats = {
  current: number;
  best: number;
};

export type PlatformSummaryResponse = {
  currentPlatformStreak: number;
  allTimePlatformStreak: number;
  questionsThisWeek: number;
  activeDaysThisWeek: number;
};

export const EMPTY_PLATFORM_SUMMARY: PlatformSummaryResponse = {
  currentPlatformStreak: 0,
  allTimePlatformStreak: 0,
  questionsThisWeek: 0,
  activeDaysThisWeek: 0,
};

export const platformSummaryToStreakStats = (
  summary: PlatformSummaryResponse
): PlatformStreakStats => ({
  current: summary.currentPlatformStreak,
  best: summary.allTimePlatformStreak,
});

export const PLACEHOLDER_PLATFORM_STREAK: PlatformStreakStats = {
  current: 3,
  best: 5,
};

export const PLACEHOLDER_ALL_TIME_STATS: AllTimeStudentStats = {
  totalDone: 1284,
  correctAnswers: 912,
  incorrectAnswers: 324,
  allTimeAvgScore: 68,
  starredQuestions: 47,
  peerAvg: 59,
  currentStreak: 3,
  bestStreak: 5,
};

export type UserStatsResponse = {
  classId: number;
  allTime: AllTimeStudentStats;
  dailyActivity: {
    last7Days: DailyReading[];
    last30Days: DailyReading[];
  };
  studyHabits: {
    last7Days: StudyHabitsPeriod;
    last30Days: StudyHabitsPeriod;
  };
  activityHeatmap: {
    days: ActivityHeatmapDay[];
  };
};

export const PLACEHOLDER_USER_STATS: UserStatsResponse = {
  classId: 42,
  allTime: PLACEHOLDER_ALL_TIME_STATS,
  dailyActivity: {
    last7Days: [
      {
        date: "2026-06-21",
        label: "Sat",
        questions: 12,
        correct: 8,
        incorrect: 4,
        accuracy: 67,
      },
      {
        date: "2026-06-22",
        label: "Sun",
        questions: 0,
        correct: 0,
        incorrect: 0,
        accuracy: 0,
      },
    ],
    last30Days: [
      {
        date: "2026-05-29",
        label: "29 May",
        questions: 16,
        correct: 11,
        incorrect: 5,
        accuracy: 69,
      },
    ],
  },
  studyHabits: {
    last7Days: {
      questionsDone: "410",
      questionsDetail: "in the last 7 days",
      correct: 267,
      incorrect: 143,
      avgScore: 65,
      peerAvg: 56,
      avgScoreDetail: "in the last 7 days",
    },
    last30Days: {
      questionsDone: "1,284",
      questionsDetail: "in the last 30 days",
      correct: 873,
      incorrect: 411,
      avgScore: 68,
      peerAvg: 61,
      avgScoreDetail: "in the last 30 days",
    },
  },
  activityHeatmap: {
    days: [
      {
        date: "2026-06-27",
        fullDateLabel: "Saturday, 27 June 2026",
        questions: 12,
      },
    ],
  },
};

export const computeAccuracyTrend = (accuracy: number[]): (number | null)[] => {
  const practicedIdx: number[] = [];
  const practicedVal: number[] = [];

  accuracy.forEach((value, index) => {
    if (value > 0) {
      practicedIdx.push(index);
      practicedVal.push(value);
    }
  });

  if (practicedIdx.length < 2) {
    return accuracy.map(() => null);
  }

  const n = practicedIdx.length;
  const sumX = practicedIdx.reduce((acc, value) => acc + value, 0);
  const sumY = practicedVal.reduce((acc, value) => acc + value, 0);
  const sumXY = practicedIdx.reduce((acc, x, i) => acc + x * practicedVal[i], 0);
  const sumX2 = practicedIdx.reduce((acc, x) => acc + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const firstIdx = practicedIdx[0];
  const lastIdx = practicedIdx[practicedIdx.length - 1];
  const trend: (number | null)[] = new Array(accuracy.length).fill(null);

  for (let i = firstIdx; i <= lastIdx; i++) {
    trend[i] = Math.round((slope * i + intercept) * 10) / 10;
  }

  return trend;
};

export const getTopicScoreColor = (score: number): string => {
  if (score >= 74) return "#1D9E75";
  if (score >= 63) return "#1B5EA6";
  if (score >= 55) return "#BA7517";
  return "#D85A30";
};

export const getAccuracyBarColor = (score: number): string => {
  // Caller handles 0% practiced days separately; 0 here means "no bar colour needed".
  if (score === 0) return "rgba(0,0,0,0)";
  if (score >= ACCURACY_STRONG_THRESHOLD) return ACCURACY_BAR_COLORS.strong;
  if (score >= ACCURACY_FAIR_THRESHOLD) return ACCURACY_BAR_COLORS.fair;
  return ACCURACY_BAR_COLORS.weak;
};

export const ACCURACY_STRONG_THRESHOLD = 70;
export const ACCURACY_FAIR_THRESHOLD = 50;

export const ACCURACY_BAR_COLORS = {
  strong: "#1D9E75",
  fair: "#BA7517",
  weak: "#D85A30",
  trend: "#0D2D6B",
  empty: "rgba(150,150,150,0.25)",
} as const;

export const ACCURACY_BAR_LEGEND = [
  { color: ACCURACY_BAR_COLORS.strong, label: "70%+", caption: "Strong" },
  { color: ACCURACY_BAR_COLORS.fair, label: "50–69%", caption: "Fair" },
  { color: ACCURACY_BAR_COLORS.weak, label: "Below 50%", caption: "Needs work" },
] as const;

export const formatPeerDiff = (score: number, peerAvg: number): { text: string; color: string } => {
  const diff = score - peerAvg;
  const color = diff >= 0 ? "#1D9E75" : "#D85A30";
  const sign = diff >= 0 ? "+" : "−";
  return { text: `${sign}${Math.abs(diff)}%`, color };
};

export type ExtendedStudentStats = {
  allTimeAvgScore: string;
  rankLabel: string;
  rankDetail: string;
  questionsThisWeek: string;
  activeStudyDays: string;
  totalStudyTime: string;
  improvement: string;
  strongestTopic: { name: string; score: string };
  weakestTopic: { name: string; score: string };
  correctAnswers: number;
  incorrectAnswers: number;
  clearedAnswers: number;
  starredQuestions: number;
  assignmentsCompleted: string;
  percentile: number;
};

export const PLACEHOLDER_STUDENT_EXTENDED_STATS: ExtendedStudentStats = {
  allTimeAvgScore: "68%",
  rankLabel: "Top 18%",
  rankDetail: "24th of 132 students",
  questionsThisWeek: "410",
  activeStudyDays: "14 / 17",
  totalStudyTime: "18h 42m",
  improvement: "+4% vs last month",
  strongestTopic: { name: "Retina", score: "82%" },
  weakestTopic: { name: "Glaucoma", score: "54%" },
  correctAnswers: 912,
  incorrectAnswers: 324,
  clearedAnswers: 48,
  starredQuestions: 47,
  assignmentsCompleted: "8 / 12",
  percentile: 82,
};

export type ExtendedTeacherStats = {
  studentsActive: string;
  avgSubmissionRate: string;
  topPerformingClass: string;
  needsAttention: string;
  questionsAssigned: string;
  avgCompletionTime: string;
};

export const PLACEHOLDER_TEACHER_EXTENDED_STATS: ExtendedTeacherStats = {
  studentsActive: "94 / 112",
  avgSubmissionRate: "78%",
  topPerformingClass: "Year 5 Ophthalmology",
  needsAttention: "3 students below 50%",
  questionsAssigned: "1,240",
  avgCompletionTime: "42 min",
};
